import { Text } from '@thanx/uikit/text'
import type { MockOrderScenario } from 'api/recoveryAi.types'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { TOUR_ANCHORS } from './tourAnchors'

const t = buildTranslate('recovery_ai.sandbox')

type Props = {
  scenario: MockOrderScenario
}

const ScenarioDetailRow = ({
  label,
  value,
}: {
  label: string
  value: string | number
}) => {
  const [css] = useStyletron()
  return (
    <div
      className={css({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        padding: '10px 0',
        borderBottom: `1px solid ${merchantTheme.colors.grey10}`,
      })}
    >
      <Text.BodyText4 color="grey70">{label}</Text.BodyText4>
      <Text.BodyText4 bold>{value}</Text.BodyText4>
    </div>
  )
}

const ScenarioDetailPanel = ({ scenario }: Props) => {
  const [css] = useStyletron()
  const { order } = scenario

  const totalDollars = `$${(order.order_total_cents / 100).toFixed(2)}`
  const deliveryLabel =
    order.delivery_method === 'delivery'
      ? t('detail_delivery')
      : t('detail_pickup')

  return (
    <div
      data-tour={TOUR_ANCHORS.scenarioDetail}
      className={css({
        backgroundColor: 'white',
        border: `1px solid ${merchantTheme.colors.grey20}`,
        borderRadius: '8px',
        padding: '24px',
        height: '100%',
      })}
    >
      {/* Scenario badge */}
      <span
        className={css({
          display: 'inline-block',
          fontWeight: 700,
          fontSize: '10px',
          lineHeight: '12px',
          letterSpacing: '2px',
          textTransform: 'uppercase',
          color: merchantTheme.colors.grey50,
          marginBottom: '12px',
        })}
      >
        {t('scenario_label')}
      </span>

      {/* Scenario name + description */}
      <Text.Header4 bold className="mb-xs">
        {scenario.scenario_name}
      </Text.Header4>
      <Text.BodyText3 color="grey70" className="mb-l">
        {scenario.scenario_description}
      </Text.BodyText3>

      {/* Order details table */}
      <div
        className={css({
          backgroundColor: merchantTheme.colors.grey05,
          borderRadius: '8px',
          padding: '16px',
        })}
      >
        <ScenarioDetailRow
          label={t('detail_customer')}
          value={order.customer_first_name}
        />
        <ScenarioDetailRow
          label={t('detail_location')}
          value={order.location_name}
        />
        <ScenarioDetailRow
          label={t('detail_order_total')}
          value={totalDollars}
        />
        <ScenarioDetailRow
          label={t('detail_delivery_method')}
          value={deliveryLabel}
        />
        <ScenarioDetailRow
          label={t('detail_order_date')}
          value={order.order_date}
        />
      </div>

      {/* Items list */}
      <div className={css({ marginTop: '20px' })}>
        <Text.BodyText4 bold className="mb-xs">
          {t('detail_items')}
        </Text.BodyText4>
        {order.items.map(item => (
          <div
            key={item.id}
            className={css({
              display: 'flex',
              justifyContent: 'space-between',
              padding: '6px 0',
              borderBottom: `1px solid ${merchantTheme.colors.grey10}`,
            })}
          >
            <Text.BodyText4 color="grey70">
              {item.quantity}x {item.name}
            </Text.BodyText4>
            <Text.BodyText4 color="grey70">
              ${(item.price_cents / 100).toFixed(2)}
            </Text.BodyText4>
          </div>
        ))}
      </div>
    </div>
  )
}

export default ScenarioDetailPanel
